import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, Grid } from '@mui/material';
import uniq from 'lodash/uniq';

import { DatatablePreview, PreviewPanel, Loader } from 'ui';
import { InvestorRelationsModal } from 'modules/common/Modals';

import { getInvestorRelationsAction } from 'store/investor/investorActions';
import { parseRelatedEntities, flattenIndustries, entityParser } from 'shared/parsers';
import { fetchEntityDetails } from 'services';

import { getAuthDataOrganisation } from 'selectors/auth';
import { IssuerDashboardHeader } from './IssuerDashboardHeader/IssuerDashboardHeader';
import { IndustryWidget } from './IndustryWidget/IndustryWidget';
import { IssuerDashboardEntitiesWidget } from './IssuerDashboardEntitiesWidget/IssuerDashboardEntitiesWidget';
import { columns } from './config';

const investorRelationsPreviewInfo = {
  count: 5,
  columns,
};

export const IssuerDashboard = () => {
  const dispatch = useDispatch();
  const [entityDetails, setEntityDetails] = useState();
  const [investorRelationsModalOpen, setInvestorRelationsModalOpen] = useState(false);
  const [industries, setIndustries] = useState([]);
  const [investorRelationsData, setInvestorRelationsData] = useState([]);

  const { relatedEntities, relatedIndustries, allIndustries } = useSelector(({ shared }) => ({
    relatedEntities: shared.relatedEntities.data,
    relatedIndustries: shared.relatedIndustries,
    allIndustries: shared.industries.data,
  }));

  const { ultimateIssuer } = useSelector(getAuthDataOrganisation);

  const { relations, isPending: investorRelationsLoading } = useSelector(
    ({ investor }) => investor,
  );

  const toggleInvestorRelationsModal = () =>
    setInvestorRelationsModalOpen(!investorRelationsModalOpen);

  const getInvestorRelations = () => dispatch(getInvestorRelationsAction());

  const getEntityDetails = () => {
    try {
      fetchEntityDetails(ultimateIssuer.id).then((data) => {
        setEntityDetails(entityParser(data));
      });
    } catch (e) {
      setEntityDetails(null);
    }
  };

  useEffect(() => {
    getEntityDetails();
    getInvestorRelations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setInvestorRelationsData(relations.slice(0, investorRelationsPreviewInfo.count));
  }, [relations]);

  useEffect(() => {
    if (!entityDetails) return;

    const flattenedIndustries = flattenIndustries(allIndustries);

    // extend related industry list with codes
    const data = uniq([entityDetails.industry, ...relatedIndustries]).reduce(
      (extendedIndustries, industryName) => {
        const industry = flattenedIndustries.find(({ name }) => name === industryName);
        if (industry) extendedIndustries.push(industry);
        return extendedIndustries;
      },
      [],
    );

    setIndustries(data);
  }, [allIndustries, relatedIndustries, entityDetails]);

  return (
    <>
      {entityDetails && (
        <IssuerDashboardHeader entity={entityDetails} entityId={ultimateIssuer.id} />
      )}
      {entityDetails === undefined && (
        <Box mb={6}>
          <Loader center />
        </Box>
      )}
      <Grid container spacing={4}>
        <Grid item xs={6}>
          <IssuerDashboardEntitiesWidget
            entities={parseRelatedEntities(relatedEntities.slice(0, 5))}
            loading={relatedEntities.isPending}
          />
        </Grid>
        <Grid item xs={6}>
          <PreviewPanel
            title="Investor relations"
            caption="Organisations which have added your companies to their portfolio"
            onViewAllClick={toggleInvestorRelationsModal}
            viewAllDisabled={investorRelationsLoading}
          >
            <DatatablePreview
              data={investorRelationsData}
              columns={investorRelationsPreviewInfo.columns}
              loading={investorRelationsLoading && !investorRelationsModalOpen}
            />
          </PreviewPanel>
        </Grid>
        {industries.map((industry) => (
          <Grid item xs={12} key={industry.name}>
            <IndustryWidget industry={industry} limit={5} ultimateIssuerId={ultimateIssuer.id} />
          </Grid>
        ))}
      </Grid>
      <InvestorRelationsModal
        open={investorRelationsModalOpen}
        onClose={toggleInvestorRelationsModal}
      />
    </>
  );
};
